import type { Board } from "../board";
import type { ParticleEmitter, SomethingWithState, TintConfig, Tintable } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { ParticleType } from "../particleType";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";

export class GoldDrop extends PhysicalThing implements Tintable, ParticleEmitter, SomethingWithState {
	static key = "GoldDrop";
	static GRAB_RADIUS = 2;
	amount: number;

	constructor(xy: XY, amount: number) {
		super(xy, false);
		this.amount = amount;
	}

	getZOrder(): number {
		return ZOrder.GOLD_DROP;
	}

	override getName(): string {
		const baseName = super.getName();
		return `${baseName} (${this.amount})`;
	}

	// Returns the coin count of the sprite that best represents this pile.
	// Exact match for 1-5 (individually countable), then bucketed by tens.
	getState(): number {
		if (this.amount >= 50) return 50;
		if (this.amount >= 40) return 40;
		if (this.amount >= 30) return 30;
		if (this.amount >= 20) return 20;
		if (this.amount >= 6) return 10;
		return Math.max(1, this.amount);
	}

	// Tintable
	getTint(): TintConfig {
		return { r: 255, g: 220, b: 50, minAlpha: 0.2, maxAlpha: 0.5 };
	}

	getAnimationSpeedMultiplier(): number {
		return 1.0;
	}

	// ParticleEmitter
	getParticleType(): ParticleType | null {
		return this.amount >= 30 ? ParticleType.GOLD_SPARKLE : null;
	}

	// Ramps linearly from 0.3 at 30 coins to 1.0 at 250 coins (expected max pile size).
	getParticleIntensity(): number {
		const t = Math.min(1, Math.max(0, (this.amount - 30) / (250 - 30)));
		return 0.3 + t * 0.7;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const amount = (props?.amount as number) ?? 1;
		const drop = new GoldDrop(xy, amount);
		board.putThing(drop);
		return [drop];
	}

	static spawnOrMerge(board: Board, xy: XY, amount: number): void {
		for (const thing of board.getThingsAt(xy)) {
			if (thing instanceof GoldDrop) {
				thing.amount += amount;
				return;
			}
		}
		board.addThing(new GoldDrop(xy, amount));
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			amount: this.amount,
		};
	}

	static deserialize(data: any): GoldDrop {
		return new GoldDrop(deserializeXY(data.xy), data.amount ?? 1);
	}
}
